import {
  computed,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { LocalStorageService } from '../local-storage/local-storage.service';

// User data interface matching localStorage structure
export interface AuthUserState {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  timezone: string;
  role: string;
  companyId: number;
  company: string;
  employeeId: number;
  approverId: number;
}

@Injectable({
  providedIn: 'root',
})
export class StateService {
  private readonly storageService = inject(LocalStorageService);

  // Private writable signals
  private readonly _token = signal<string | null>(null);
  private readonly _user = signal<AuthUserState | null>(null);

  // ==================== Public Readonly Signals ====================
  // Token
  readonly token = this._token.asReadonly();

  // User object
  readonly user = this._user.asReadonly();

  // Individual user properties as computed signals
  readonly userId = computed(() => this._user()?.id ?? null);
  readonly email = computed(() => this._user()?.email ?? null);
  readonly firstName = computed(() => this._user()?.firstName ?? null);
  readonly lastName = computed(() => this._user()?.lastName ?? null);
  readonly fullName = computed(() => {
    const user = this._user();
    if (!user) return null;
    return `${user.firstName} ${user.lastName}`.trim();
  });
  readonly timezone = computed(() => this._user()?.timezone ?? null);
  readonly role = computed(() => this._user()?.role ?? null);
  readonly companyId = computed(() => this._user()?.companyId ?? null);
  readonly companyName = computed(() => this._user()?.company ?? null);
  readonly employeeId = computed(() => this._user()?.employeeId ?? null);
  readonly approverId = computed(() => this._user()?.approverId ?? null);

  // Utility computed signals
  readonly isAuthenticated = computed(() => !!this._token());
  readonly isAdmin = computed(() => this._user()?.role === 'administrator');
  readonly isSupervisor = computed(() => this._user()?.role === 'supervisor');
  readonly isEmployee = computed(() => this._user()?.role === 'employee');

  constructor() {
    // Initialize state from localStorage on service creation
    this.listentoStorage();
    this.loadFromStorage();
  }

  // ==================== Public Methods ====================

  /**
   * Set the auth token and persist to localStorage
   */
  setToken(token: string): void {
    this._token.set(token);
    this.storageService.setItem('auth_token', token);
  }

  /**
   * Set the user data and persist to localStorage
   */
  setUser(user: AuthUserState): void {
    this._user.set(user);
    this.storageService.setItem('auth_user', user);
  }

  /**
   * Set both token and user at once (useful after login)
   */
  setAuthState(token: string, user: AuthUserState): void {
    this.setToken(token);
    this.setUser(user);
  }

  /**
   * Clear all auth state (useful for logout)
   */
  clearState(): void {
    this._token.set(null);
    this._user.set(null);
    this.storageService.removeItem('auth_token');
    this.storageService.removeItem('auth_user');
  }

  /**
   * Reload state from localStorage (useful after external changes)
   */
  refreshFromStorage(): void {
    this.loadFromStorage();
  }

  // ==================== Private Methods ====================

  private listentoStorage() {
    // This could be used to listen to storage events if needed
  }

  /**
   * Load auth state from localStorage
   */
  private loadFromStorage(): void {
    // Load token
    const token = this.storageService.getItem<string>('auth_token');
    if (token) {
      this._token.set(token);
    }

    // Load user
    const user = this.storageService.getItem<AuthUserState>('auth_user');
    if (user) {
      this._user.set(user);
    }
  }
}
